import { FC, useEffect } from 'react';
import { Grid, Typography, Divider } from '@mui/material';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import dayjs, { Dayjs } from 'dayjs';
import StructuredModal from '../../../../components/structuredModal/structuredModal';
import ControlledTextField from '../../../../components/controlledComponents/ControlledTextField.component';
import ControlledSelectField from '../../../../components/controlledComponents/ControlledSelectField.component';
import ControlledDateField from '../../../../components/controlledComponents/ControlledDateField.component';
import { useTranslation } from 'src/hooks/useTranslationHook';
import { TTranslateTextType } from 'src/hooks/useTranslationHook';
import { IInstituteLeaveType } from '../../../../../apis/institute/institute.interfaces';
import {
  isPickerDateRangeOrdered,
  zRequiredPickerDate,
} from 'src/utils/zodFormHelpers';

export interface ApplyLeaveFormData {
  leaveTypeId: string;
  startDate: Dayjs | null;
  endDate: Dayjs | null;
  reason?: string;
}

export interface ApplyLeaveSubmitPayload {
  leaveTypeId: string;
  startDate: string;
  endDate: string;
  reason?: string;
}

interface ApplyLeaveFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: ApplyLeaveSubmitPayload) => void;
  leaveTypes: IInstituteLeaveType[];
  isLoading?: boolean;
  errorMessage?: string;
}

const getApplyLeaveSchema = (translateText: TTranslateTextType) =>
  z
    .object({
      leaveTypeId: z.string().min(1, translateText('profile.leaves.validation.leaveTypeRequired')),
      startDate: zRequiredPickerDate({
        requiredMessage: translateText('profile.leaves.validation.startDateRequired'),
        invalidMessage: translateText('common.dateInvalid'),
      }),
      endDate: zRequiredPickerDate({
        requiredMessage: translateText('profile.leaves.validation.endDateRequired'),
        invalidMessage: translateText('common.dateInvalid'),
      }),
      reason: z.string().optional(),
    })
    .refine(
      (data) => isPickerDateRangeOrdered(data.startDate, data.endDate),
      {
        message: translateText('profile.leaves.validation.endDateAfterStart'),
        path: ['endDate'],
      }
    );

const ApplyLeaveForm: FC<ApplyLeaveFormProps> = ({
  isOpen,
  onClose,
  onSubmit,
  leaveTypes,
  isLoading = false,
  errorMessage,
}) => {
  const { translateText } = useTranslation();
  const schema = getApplyLeaveSchema(translateText);

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<ApplyLeaveFormData>({
    resolver: zodResolver(schema),
    mode: 'onBlur',
    defaultValues: {
      leaveTypeId: '',
      startDate: null,
      endDate: null,
      reason: '',
    },
  });

  useEffect(() => {
    if (!isOpen) reset();
  }, [isOpen, reset]);

  const leaveTypeOptions = leaveTypes.map((lt) => ({
    key: lt.name,
    value: lt.id,
  }));

  const handleFormSubmit = (data: ApplyLeaveFormData) => {
    if (!data.startDate || !data.endDate) return;
    onSubmit({
      leaveTypeId: data.leaveTypeId,
      startDate: dayjs(data.startDate).format('YYYY-MM-DD'),
      endDate: dayjs(data.endDate).format('YYYY-MM-DD'),
      reason: data.reason,
    });
  };

  return (
    <StructuredModal
      isOpen={isOpen}
      onClose={onClose}
      title={translateText('profile.leaves.applyLeave')}
      footerPositiveAction={handleSubmit(handleFormSubmit)}
      footerPositiveActionText={translateText('profile.leaves.submit')}
      footerNegativeAction={onClose}
      footerNegativeActionText={translateText('common.cancel')}
      footerMessage={
        errorMessage ? (
          <Typography color="error" variant="body2">
            {errorMessage}
          </Typography>
        ) : null
      }
      isLoading={isLoading}
    >
      <form onSubmit={handleSubmit(handleFormSubmit)} style={{ width: '100%' }}>
        <Grid
          container
          spacing={{ xs: 2, sm: 3 }}
          sx={{ p: { xs: 1, sm: 2 } }}
          data-testid="apply-leave-form-container"
        >
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              {translateText('profile.leaves.formTitle')}
            </Typography>
            <Divider sx={{ mb: 1.5 }} />
          </Grid>
          <Grid item xs={12}>
            <ControlledSelectField
              id="leaveTypeId"
              label={translateText('profile.leaves.leaveType')}
              name="leaveTypeId"
              control={control}
              itemList={leaveTypeOptions}
              dataTestId="leave-type-select"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <ControlledDateField
              id="startDate"
              name="startDate"
              label={translateText('profile.leaves.startDate')}
              control={control}
              errorMessage={errors.startDate?.message}
              data-testid="leave-start-date"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <ControlledDateField
              id="endDate"
              name="endDate"
              label={translateText('profile.leaves.endDate')}
              control={control}
              errorMessage={errors.endDate?.message}
              data-testid="leave-end-date"
            />
          </Grid>
          <Grid item xs={12}>
            <ControlledTextField
              id="reason"
              label={translateText('profile.leaves.reason')}
              name="reason"
              control={control}
              errorMessage={errors.reason?.message}
              multiline
              rows={3}
              dataTestId="leave-reason-input"
            />
          </Grid>
        </Grid>
      </form>
    </StructuredModal>
  );
};

export default ApplyLeaveForm;
